import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, FileText, Plus, Trash2, Search, Building2, Calendar, Package, X, IndianRupee, Clock, CheckCircle, Percent, Mail, Phone } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const PAYMENT_TERMS = [
  { value: 'due_on_receipt', label: 'Due on Receipt' },
  { value: 'net_7', label: 'Net 7 Days' },
  { value: 'net_15', label: 'Net 15 Days' },
  { value: 'net_30', label: 'Net 30 Days' },
  { value: 'net_45', label: 'Net 45 Days' },
  { value: 'net_60', label: 'Net 60 Days' },
];

const CreatePurchaseInvoicePage = () => {
  const { businessId, id: invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;
  const isEdit = !!invoiceId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [debouncedSupplierSearch, setDebouncedSupplierSearch] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const supplierDebounceRef = useRef(null);
  const [sourceInfo, setSourceInfo] = useState({ type: null, id: null, document: null });
  const [itemSearch, setItemSearch] = useState('');
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const itemSearchRef = useRef(null);

  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    supplier_gstin: '',
    supplier_address: '',
    supplier_phone: '',
    supplier_email: '',
    invoice_number: '',
    supplier_invoice_no: '',
    invoice_date: new Date().toISOString().split('T')[0],
    payment_terms: 'net_30',
    due_date: '',
    place_of_supply: '',
    purchase_type: 'goods',
    notes: '',
    terms: '',
    discount_type: 'percent',
    discount_value: 0,
    shipping_charges: 0,
    other_charges: 0,
    round_off: 0,
    items: [{ item_id: '', item_name: '', item_code: '', hsn_sac: '', quantity: 1, unit: 'PCS', purchase_rate: 0, discount_percent: 0, discount_amount: 0, tax_percent: 0, tax_amount: 0, line_total: 0 }]
  });

  useEffect(() => {
    fetchSuppliers();
    fetchItems();
    if (isEdit) fetchInvoice();
    
    const sourceType = searchParams.get('source_type');
    const sourceId = searchParams.get('source_id');
    if (sourceType && sourceId) {
      setSourceInfo({ type: sourceType, id: sourceId, document: null });
      fetchSourceDocument(sourceType, sourceId);
    }
  }, [isEdit]);

  const fetchSourceDocument = async (sourceType, sourceId) => {
    try {
      const endpoint = sourceType === 'PO'
        ? `/purchase-orders/${sourceId}`
        : `/purchase-receives/${sourceId}`;

      const { data } = await api.get(endpoint);
      setSourceInfo(prev => ({ ...prev, document: data }));

      const invoiceItems = (data.items || []).map(item => ({
        item_id: item.item_id || '',
        item_name: item.item_name || '',
        item_code: item.item_code || '',
        hsn_sac: item.hsn_code || item.hsn_sac || '',
        quantity: sourceType === 'RECEIVE' ? (item.received_quantity || item.quantity || 1) : (item.quantity || 1),
        unit: item.unit || 'PCS',
        purchase_rate: item.rate || item.purchase_rate || 0,
        discount_percent: item.discount_percent || 0,
        discount_amount: item.discount_amount || 0,
        tax_percent: item.tax_percent || item.gst_rate || 0,
        tax_amount: item.tax_amount || 0,
        line_total: item.amount || item.line_total || 0
      }));

      setFormData(prev => ({
        ...prev,
        supplier_id: data.supplier_id || '',
        supplier_name: data.supplier_name || '',
        supplier_gstin: data.supplier_gstin || '',
        supplier_address: data.supplier_address || '',
        supplier_phone: data.supplier_phone || '',
        supplier_email: data.supplier_email || '',
        source_type: sourceType,
        source_id: sourceId,
        source_number: sourceType === 'PO' ? data.order_number : data.receive_number,
        supplier_invoice_no: sourceType === 'PO' ? data.order_number : data.receive_number,
        items: invoiceItems.length > 0 ? invoiceItems : prev.items
      }));
      setSupplierSearch(data.supplier_name || '');
    } catch (e) {
      console.error('Error fetching source document:', e);
      toast.error('Failed to load source document');
    }
  };

  useEffect(() => {
    if (formData.invoice_date && formData.payment_terms) {
      const invoiceDate = new Date(formData.invoice_date);
      const termsDays = { due_on_receipt: 0, net_7: 7, net_15: 15, net_30: 30, net_45: 45, net_60: 60 };
      const days = termsDays[formData.payment_terms] || 30;
      const dueDate = new Date(invoiceDate);
      dueDate.setDate(dueDate.getDate() + days);
      setFormData(prev => ({ ...prev, due_date: dueDate.toISOString().split('T')[0] }));
    }
  }, [formData.invoice_date, formData.payment_terms]);

  const fetchSuppliers = async () => {
    try {
      const { data } = await api.get('/parties', { params: { type: 'supplier' } });
      setSuppliers(data.parties || data || []);
    } catch (e) { console.error(e); }
  };

  const fetchItems = async () => {
    try {
      const { data } = await api.get('/items');
      setItems(data.items || data || []);
    } catch (e) { console.error(e); }
  };

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/purchase-invoices/${invoiceId}`);
      setFormData({ ...data, items: data.items?.length ? data.items : formData.items });
      setSupplierSearch(data.supplier_name || '');
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const selectSupplier = (supplier) => {
    setFormData(prev => ({
      ...prev,
      supplier_id: supplier.id,
      supplier_name: supplier.name,
      supplier_gstin: supplier.gstin || '',
      supplier_address: supplier.billing_address || supplier.address || '',
      supplier_phone: supplier.phone || '',
      supplier_email: supplier.email || ''
    }));
    setSupplierSearch(supplier.name);
    setDebouncedSupplierSearch(supplier.name);
    setShowSupplierDropdown(false);
  };

  const calculateLineTotal = (item) => {
    const subtotal = item.quantity * item.purchase_rate;
    const discountAmt = item.discount_percent > 0 ? subtotal * (item.discount_percent / 100) : item.discount_amount;
    const afterDiscount = subtotal - discountAmt;
    const taxAmt = afterDiscount * (item.tax_percent / 100);
    return { line_total: afterDiscount + taxAmt, discount_amount: discountAmt, tax_amount: taxAmt };
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      const calcs = calculateLineTotal(newItems[index]);
      newItems[index] = { ...newItems[index], ...calcs };
      return { ...prev, items: newItems };
    });
  };

  const selectItem = (index, item) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        item_id: item.id,
        item_name: item.name,
        item_code: item.sku || item.item_code || '',
        hsn_sac: item.hsn_code || item.sac_code || '',
        unit: item.unit || 'PCS',
        purchase_rate: item.purchase_price || item.cost_price || 0,
        tax_percent: item.tax_rate || item.gst_rate || 0
      };
      const calcs = calculateLineTotal(newItems[index]);
      newItems[index] = { ...newItems[index], ...calcs };
      return { ...prev, items: newItems };
    });
  };

  // Add item from search dropdown -- creates a new row with the item pre-filled
  const addItemFromSearch = (product) => {
    const newItem = {
      item_id: product.id,
      item_name: product.name,
      item_code: product.sku || product.item_code || '',
      hsn_sac: product.hsn_code || product.sac_code || '',
      quantity: 1,
      unit: product.unit || 'PCS',
      purchase_rate: product.purchase_price || product.cost_price || 0,
      discount_percent: 0,
      discount_amount: 0,
      tax_percent: product.tax_rate || product.gst_rate || 0,
      tax_amount: 0,
      line_total: 0,
    };
    const calcs = calculateLineTotal(newItem);
    setFormData(prev => ({
      ...prev,
      items: [...prev.items.filter(i => i.item_name), { ...newItem, ...calcs }],
    }));
    setItemSearch('');
    setShowItemDropdown(false);
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { item_id: '', item_name: '', item_code: '', hsn_sac: '', quantity: 1, unit: 'PCS', purchase_rate: 0, discount_percent: 0, discount_amount: 0, tax_percent: 0, tax_amount: 0, line_total: 0 }]
    }));
  };

  // Click-outside for item search
  useEffect(() => {
    const handler = (e) => {
      if (itemSearchRef.current && !itemSearchRef.current.contains(e.target)) {
        setShowItemDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.purchase_rate), 0);
  const itemDiscountTotal = formData.items.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const invoiceDiscount = formData.discount_type === 'percent' ? subtotal * (formData.discount_value / 100) : formData.discount_value;
  const taxTotal = formData.items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const grandTotal = subtotal - itemDiscountTotal - invoiceDiscount + taxTotal + parseFloat(formData.shipping_charges || 0) + parseFloat(formData.other_charges || 0) + parseFloat(formData.round_off || 0);

  const handleSubmit = async (asDraft = false) => {
    if (!formData.supplier_id) { toast.error('Please select a supplier'); return; }
    if (!formData.supplier_invoice_no) { toast.error('Supplier Invoice No is required'); return; }
    if (formData.items.every(i => !i.item_name)) { toast.error('Please add at least one item'); return; }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        subtotal,
        discount_amount: itemDiscountTotal + invoiceDiscount,
        tax_total: taxTotal,
        grand_total: grandTotal,
        save_as_draft: asDraft,
      };

      if (isEdit) {
        await api.put(`/purchase-invoices/${invoiceId}`, payload);
      } else {
        await api.post('/purchase-invoices', payload);
      }

      toast.success(isEdit ? 'Invoice updated' : 'Invoice created');
      navigate(`/business/${bid}/purchase-invoices`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Error saving invoice'));
    }
    setSaving(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading invoice...</p>
        </div>
      </div>
    );
  }

  const handleSupplierSearch = useCallback((value) => {
    setSupplierSearch(value);
    setShowSupplierDropdown(true);
    if (supplierDebounceRef.current) clearTimeout(supplierDebounceRef.current);
    supplierDebounceRef.current = setTimeout(() => setDebouncedSupplierSearch(value), 500);
  }, []);

  const filteredSuppliers = suppliers.filter(s => s.name?.toLowerCase().includes(debouncedSupplierSearch.toLowerCase()));

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* PREMIUM HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[20px] font-bold text-slate-800">{isEdit ? 'Edit Purchase Invoice' : 'New Purchase Invoice'}</h1>
                  {sourceInfo.type && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold bg-blue-100 text-blue-700">
                      <FileText className="w-3.5 h-3.5" />
                      From {sourceInfo.type === 'PO' ? 'Purchase Order' : 'Purchase Receive'}
                    </span>
                  )}
                </div>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  Record supplier bill {sourceInfo.document && `• Source: ${formData.source_number}`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSubmit(true)}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
              >
                <Clock className="w-4 h-4" />
                Save Draft
              </button>
              <button
                onClick={() => handleSubmit(false)}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20 active:scale-[0.98]"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Invoice'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT - Premium 3-Column Layout */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - Supplier & Invoice Info (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Supplier Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Supplier (Bill From)</h2>
                </div>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search supplier..."
                    value={supplierSearch}
                    onChange={(e) => handleSupplierSearch(e.target.value)}
                    onFocus={() => setShowSupplierDropdown(true)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                  />
                  {showSupplierDropdown && filteredSuppliers.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {filteredSuppliers.map(s => (
                        <div key={s.id} onClick={() => selectSupplier(s)} className="px-4 py-3 hover:bg-teal-50 cursor-pointer text-[13px] flex items-center gap-3 border-b border-slate-100 last:border-0">
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                            <span className="text-[12px] font-bold text-teal-700">{s.name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{s.name}</p>
                            {s.gstin && <p className="text-[11px] text-slate-500">GSTIN: {s.gstin}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {formData.supplier_id && (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-[16px] font-bold text-teal-700">{formData.supplier_name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{formData.supplier_name}</p>
                        <p className="text-[12px] text-slate-500">Selected Supplier</p>
                      </div>
                    </div>
                    {formData.supplier_gstin && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        GSTIN: {formData.supplier_gstin}
                      </div>
                    )}
                    {formData.supplier_phone && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {formData.supplier_phone}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Invoice Info Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Invoice Details</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-[12px] text-slate-500 block mb-1">Supplier Invoice No *</label>
                    {sourceInfo.type ? (
                      <div className="w-full px-3 py-2.5 bg-teal-50 border border-teal-200 rounded-xl text-[13px] text-teal-700 font-medium">
                        Auto: {formData.source_number || 'From ' + (sourceInfo.type === 'PO' ? 'Purchase Order' : 'Purchase Receive')}
                      </div>
                    ) : (
                      <input
                        type="text"
                        placeholder="Enter vendor's bill number"
                        value={formData.supplier_invoice_no}
                        onChange={(e) => setFormData(prev => ({ ...prev, supplier_invoice_no: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      />
                    )}
                    {sourceInfo.type && (
                      <p className="text-[11px] text-teal-600 mt-1">Linked to {sourceInfo.type === 'PO' ? 'PO' : 'GRN'}: {formData.source_number}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[12px] text-slate-500 block mb-1">Invoice Date</label>
                      <input
                        type="date"
                        value={formData.invoice_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="text-[12px] text-slate-500 block mb-1">Due Date</label>
                      <input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[12px] text-slate-500 block mb-1">Payment Terms</label>
                    <select
                      value={formData.payment_terms}
                      onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    >
                      {PAYMENT_TERMS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[12px] text-slate-500 block mb-1">Purchase Type</label>
                    <select
                      value={formData.purchase_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, purchase_type: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    >
                      <option value="goods">Goods</option>
                      <option value="services">Services</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Notes Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Notes</h2>
                </div>
                <textarea
                  placeholder="Internal notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
                />
              </div>
            </div>

            {/* CENTER COLUMN - Items Table (6 cols) */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                      <Package className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Invoice Items</h2>
                      <p className="text-[12px] text-slate-500">{formData.items.filter(i => i.item_name).length} item(s)</p>
                    </div>
                  </div>
                </div>

                {/* Item search bar -- ABOVE the table, not inside rows */}
                <div className="px-5 py-3 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 relative" ref={itemSearchRef}>
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={itemSearch}
                        onChange={(e) => { setItemSearch(e.target.value); setShowItemDropdown(true); }}
                        onFocus={() => setShowItemDropdown(true)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 focus:outline-none"
                        placeholder="Search items by name or code to add..."
                      />
                      {showItemDropdown && (() => {
                        const query = itemSearch.toLowerCase();
                        const filtered = items.filter(i => !query || i.name?.toLowerCase().includes(query) || (i.sku || '').toLowerCase().includes(query)).slice(0, 8);
                        return filtered.length > 0 ? (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                            {filtered.map((product, idx) => (
                              <div key={product.id} onClick={() => addItemFromSearch(product)}
                                className={`px-4 py-3 hover:bg-teal-50 cursor-pointer transition-colors ${idx !== 0 ? 'border-t border-slate-100' : ''}`}>
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-[13px] font-medium text-slate-800 truncate">{product.name}</p>
                                  {(product.sku || product.item_code) && <p className="text-[11px] text-slate-400 shrink-0">{product.sku || product.item_code}</p>}
                                </div>
                              </div>
                            ))}
                            {items.length > 8 && (
                              <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-400 text-center">
                                Showing 8 of {items.length} -- type to narrow
                              </div>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>

                {/* Items table -- just editable rows for already-added items */}
                {formData.items.filter(i => i.item_name).length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px]">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-[180px]">Item</th>
                        <th className="px-2 py-3 text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-[60px]">Qty</th>
                        <th className="px-2 py-3 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-[80px]">Rate</th>
                        <th className="px-2 py-3 text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-[55px]">Tax%</th>
                        <th className="px-3 py-3 text-right text-[10px] font-semibold text-teal-600 uppercase tracking-wide w-[90px]">Amount</th>
                        <th className="px-2 py-3 w-[40px]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {formData.items.filter(i => i.item_name).map((item, index) => (
                        <tr key={index} className="hover:bg-slate-50/50 transition-colors duration-150">
                          <td className="px-3 py-3">
                            <p className="text-[13px] font-medium text-slate-800">{item.item_name}</p>
                            {item.hsn_sac && <p className="text-[10px] text-slate-400 mt-0.5">HSN: {item.hsn_sac}</p>}
                          </td>
                          <td className="px-2 py-3">
                            <input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)} className="w-full px-2 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-center" />
                          </td>
                          <td className="px-2 py-3">
                            <input type="number" min="0" step="0.01" value={item.purchase_rate} onChange={(e) => updateItem(index, 'purchase_rate', parseFloat(e.target.value) || 0)} className="w-full px-2 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-right" />
                          </td>
                          <td className="px-2 py-3">
                            <input type="number" min="0" max="100" value={item.tax_percent} onChange={(e) => updateItem(index, 'tax_percent', parseFloat(e.target.value) || 0)} className="w-full px-2 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-center" placeholder="0" />
                          </td>
                          <td className="px-3 py-3 text-right">
                            <span className="text-[14px] font-bold text-teal-600">{'\u20B9'}{(item.line_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </td>
                          <td className="px-2 py-3">
                            <button onClick={() => removeItem(index)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            </div>
            {/* RIGHT COLUMN - Summary (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Summary Card */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-5 shadow-sm border border-teal-200/60">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-sm">
                    <IndianRupee className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Invoice Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  {itemDiscountTotal > 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-600">Item Discount</span>
                      <span className="text-red-600 font-medium">- ₹{itemDiscountTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  
                  {/* Invoice Discount */}
                  <div className="flex justify-between items-center text-[13px]">
                    <span className="text-slate-600">Discount</span>
                    <div className="flex items-center gap-2">
                      <select value={formData.discount_type} onChange={(e) => setFormData(prev => ({ ...prev, discount_type: e.target.value }))} className="px-2 py-1 bg-white border border-slate-200 rounded text-[11px]">
                        <option value="percent">%</option>
                        <option value="fixed">₹</option>
                      </select>
                      <input type="number" min="0" value={formData.discount_value} onChange={(e) => setFormData(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))} className="w-16 px-2 py-1 bg-white border border-slate-200 rounded text-[12px] text-right" />
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-emerald-600 font-medium">+ ₹{taxTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  {/* Shipping */}
                  <div className="flex justify-between items-center text-[13px]">
                    <span className="text-slate-600">Shipping</span>
                    <input type="number" min="0" value={formData.shipping_charges} onChange={(e) => setFormData(prev => ({ ...prev, shipping_charges: parseFloat(e.target.value) || 0 }))} className="w-20 px-2 py-1 bg-white border border-slate-200 rounded text-[12px] text-right" />
                  </div>
                  
                  {/* Other Charges */}
                  <div className="flex justify-between items-center text-[13px]">
                    <span className="text-slate-600">Other Charges</span>
                    <input type="number" value={formData.other_charges} onChange={(e) => setFormData(prev => ({ ...prev, other_charges: parseFloat(e.target.value) || 0 }))} className="w-20 px-2 py-1 bg-white border border-slate-200 rounded text-[12px] text-right" />
                  </div>
                  
                  {/* Round Off */}
                  <div className="flex justify-between items-center text-[13px]">
                    <span className="text-slate-600">Round Off</span>
                    <input type="number" step="0.01" value={formData.round_off} onChange={(e) => setFormData(prev => ({ ...prev, round_off: parseFloat(e.target.value) || 0 }))} className="w-20 px-2 py-1 bg-white border border-slate-200 rounded text-[12px] text-right" />
                  </div>
                  
                  <div className="border-t border-teal-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Grand Total</span>
                      <span className="text-[22px] font-bold text-teal-600">
                        ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Balance Due Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Payment Status</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Balance Due</span>
                    <span className="text-[18px] font-bold text-amber-600">₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Due Date</span>
                    <span className="text-slate-700 font-medium">{formatDate(formData.due_date)}</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <h2 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  <button
                    onClick={() => handleSubmit(true)}
                    disabled={saving}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors duration-200"
                  >
                    <Clock className="w-4 h-4 text-slate-600" />
                    <span className="text-[13px] font-medium text-slate-700">Save as Draft</span>
                  </button>
                  <button
                    onClick={() => handleSubmit(false)}
                    disabled={saving}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-teal-50 hover:bg-teal-100 transition-colors duration-200"
                  >
                    <CheckCircle className="w-4 h-4 text-teal-600" />
                    <span className="text-[13px] font-medium text-teal-700">Save & Confirm</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePurchaseInvoicePage;
